import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';

import { useLetterhead } from '@/api/bookings';
import { useGarmentMovement, useGarmentMovementDay, type GarmentMovementDay } from '@/api/reports';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import type { GarmentMovementCounts } from '@core/api/reports';
import {
  countCell,
  dayLabel,
  lastSevenDays,
  movementColumns,
  movementWords,
  rangeProblem,
  registerSentence,
  registerSheet,
} from '@core/reports/garmentMovement';
import type { Session } from '@core/session/types';
import { PrintFrame } from '../print/PrintFrame';
import { reportPaperFor, reportPapers } from '../print/paper';
import { GarmentMovementSheet } from '../print/GarmentMovementSheet';
import { saveWorkbook } from '../units/import/workbook';
import { DayMovesDialog } from './DayMovesDialog';

/** A register row: a shop day, or the totals under them. */
interface Row {
  id: string;
  day: string | null;
  counts: GarmentMovementCounts;
}

const iso = (value: Dayjs) => value.format('YYYY-MM-DD');

/**
 * Reports › Garments in and out — report A1, direction A of canvas "Daily garment in/out" (owner,
 * 2026-10-29): the range on the band with Print and Excel, a row per shop day with seven counts and
 * the totals, and a day opened in a popup, `DayMovesDialog` (direction D, owner, 2026-10-29 — was a
 * card under the table). Every figure is the server's; the words and the order are the core's
 * (`reports/garmentMovement.ts`).
 */
export function GarmentMovementReport({
  session,
  onBack,
  onOpen,
}: {
  session: Session;
  onBack: () => void;
  onOpen: (key: string, view?: string, find?: string, record?: number) => void;
}) {
  const branchId = session.branch?.id;
  const today = iso(dayjs());
  const [range, setRange] = useState(() => lastSevenDays(today));
  const [opened, setOpened] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  const problem = rangeProblem(range.from, range.to);
  const report = useGarmentMovement(branchId, range.from, range.to, problem === null);
  const day = useGarmentMovementDay(branchId, opened);
  const letterhead = useLetterhead(branchId);
  const branchName = session.branch?.name ?? '';

  if (printing && report.data) {
    const shown = report.data;

    return (
      <PrintFrame
        onClose={() => setPrinting(false)}
        title="Garments in and out"
        sizes={reportPapers}
        start={reportPaperFor(movementColumns.length + 1)}
      >
        {(paper) => (
          <GarmentMovementSheet
            paper={paper}
            report={shown}
            letterhead={letterhead.data}
            branchName={branchName}
          />
        )}
      </PrintFrame>
    );
  }

  const pick = (end: 'from' | 'to') => (value: Dayjs | null) => {
    if (value) {
      setRange((current) => ({ ...current, [end]: iso(value) }));
      setOpened(null);
    }
  };

  const exportSheet = () => {
    if (!report.data) {
      return;
    }

    const sheet = registerSheet(report.data);

    void saveWorkbook(
      `garments-in-and-out-${report.data.from}-to-${report.data.to}.xlsx`,
      'Garments in and out',
      sheet.headers,
      sheet.rows,
    );
  };

  const rows: Row[] = report.data
    ? [
        ...report.data.days.map((d: GarmentMovementDay) => ({
          id: d.day,
          day: d.day,
          counts: d.counts,
        })),
        { id: 'total', day: null, counts: report.data.total },
      ]
    : [];
  const dayCount = report.data?.days.length ?? 0;
  const openedRow = report.data?.days.find((d: GarmentMovementDay) => d.day === opened);

  const columns: DataViewColumn<Row>[] = [
    {
      title: 'Day',
      key: 'day',
      primary: true,
      render: (_, row) =>
        row.day === null ? (
          <span className="text-xs font-bold text-gray-900">
            {dayCount} day{dayCount === 1 ? '' : 's'}
          </span>
        ) : (
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => setOpened(row.day)}
            className="hover:text-primary inline-flex items-center gap-1.5 text-xs font-semibold text-gray-900"
          >
            <i className="icon icon-chevron-right" aria-hidden="true" />
            {dayLabel(row.day)}
          </button>
        ),
    },
    ...movementColumns.map((column): DataViewColumn<Row> => ({
      title: movementWords[column.kind],
      key: column.key,
      align: 'right',
      render: (_, row) => (
        <span
          className={`font-mono-nums text-xs ${row.day === null ? 'font-bold text-gray-900' : ''}`}
        >
          {row.day === null ? row.counts[column.key] : countCell(row.counts[column.key])}
        </span>
      ),
    })),
  ];

  return (
    <>
      <PageHeading
        eyebrow="Reports · Stock"
        title="Garments in and out"
        subtitle={report.data ? registerSentence(report.data) : problem ? '' : 'Reading the days…'}
        actions={
          <>
            <label className="flex flex-col gap-1 text-[11px] text-[#ffffff]">
              From
              <DatePicker
                value={dayjs(range.from)}
                allowClear={false}
                disabledDate={(value) => value.isAfter(dayjs(), 'day')}
                onChange={pick('from')}
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-[#ffffff]">
              To
              <DatePicker
                value={dayjs(range.to)}
                allowClear={false}
                disabledDate={(value) => value.isAfter(dayjs(), 'day')}
                onChange={pick('to')}
              />
            </label>
            <Button
              icon="icon icon-printer"
              shortcut="print"
              disabled={!report.data}
              onClick={() => setPrinting(true)}
            >
              Print
            </Button>
            <Button icon="icon icon-file-spreadsheet" disabled={!report.data} onClick={exportSheet}>
              Excel
            </Button>
            <Button icon="icon icon-arrow-left" shortcut="back" onClick={onBack}>
              Reports
            </Button>
          </>
        }
      />

      {problem && (
        <div className="mb-4">
          <Notice tone="warning" title={problem}>
            Choose a shorter range — the register reads every movement in it at once.
          </Notice>
        </div>
      )}

      <Card flush>
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4 pb-2">
          <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            Day by day
          </p>
          <span className="text-default text-[11px]">Open a day to see which garments moved</span>
        </div>
        <DataView
          columns={columns}
          dataSource={rows}
          loading={report.isLoading}
          empty={
            <EmptyState
              icon="icon icon-shirt"
              title={report.isError ? 'The report could not be read' : 'No days to show'}
            />
          }
        />
      </Card>

      {openedRow && (
        <DayMovesDialog
          day={openedRow.day}
          counts={openedRow.counts}
          branchName={branchName}
          detail={day.data}
          loading={day.isLoading}
          onClose={() => setOpened(null)}
          onOpenBooking={(rentalOrderId) => onOpen('bookings', undefined, undefined, rentalOrderId)}
        />
      )}

      <p className="text-default mt-3 text-[11px]">
        Days are the shop’s own. A garment counts when it crosses the door: handed over or taken
        back at the counter, sent to or back from the cleaner or tailor. Booked in counts new
        garments into stock — a supplier’s delivery or an import.
      </p>
    </>
  );
}
